// Content loader using Vite's import.meta.glob. All markdown files in
// content/insights/ are bundled at build time, parsed for frontmatter,
// and exposed as a sorted array.

import { parseFrontmatter, readingStats } from "./frontmatter";

export interface ArticleFrontmatter {
  title: string;
  slug: string;
  description: string;
  author: string;
  authorTitle?: string;
  category: "guides" | "market-insights" | "cost-breakdown" | "product-news";
  tags: string[];
  publishedAt: string; // ISO date
  updatedAt?: string;
  heroImage?: string;
  featured?: boolean;
}

export interface Article extends ArticleFrontmatter {
  content: string;
  readingMinutes: number;
  wordCount: number;
}

interface RawModule {
  default: string;
}

// Vite imports all .md files as raw strings at build time
const modules = import.meta.glob("/content/insights/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

let cache: Article[] | null = null;

function parseArticles(): Article[] {
  if (cache) return cache;

  const articles: Article[] = [];

  for (const [path, raw] of Object.entries(modules)) {
    const content = typeof raw === "string" ? raw : (raw as RawModule).default;
    const { data, content: body } = parseFrontmatter(content);
    const frontmatter = data as unknown as ArticleFrontmatter;

    if (!frontmatter.slug) {
      const filename = path.split("/").pop()?.replace(".md", "") ?? "";
      frontmatter.slug = filename;
    }

    const stats = readingStats(body);
    articles.push({
      ...frontmatter,
      content: body,
      readingMinutes: stats.minutes,
      wordCount: stats.words,
    });
  }

  articles.sort(
    (a, b) =>
      new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
  );
  cache = articles;
  return articles;
}

export function getAllArticles(): Article[] {
  return parseArticles();
}

export function getArticleBySlug(slug: string): Article | undefined {
  return parseArticles().find((a) => a.slug === slug);
}

export function getArticlesByCategory(
  category: ArticleFrontmatter["category"],
): Article[] {
  return parseArticles().filter((a) => a.category === category);
}

export function getFeaturedArticles(limit = 3): Article[] {
  return parseArticles()
    .filter((a) => a.featured)
    .slice(0, limit);
}

export function getRelatedArticles(slug: string, limit = 3): Article[] {
  const target = getArticleBySlug(slug);
  if (!target) return [];

  return parseArticles()
    .filter((a) => a.slug !== slug)
    .sort((a, b) => {
      // Same category first
      const aCat = a.category === target.category ? 2 : 0;
      const bCat = b.category === target.category ? 2 : 0;
      // Tag overlap
      const aTags = a.tags.filter((t) => target.tags.includes(t)).length;
      const bTags = b.tags.filter((t) => target.tags.includes(t)).length;
      return bCat + bTags - (aCat + aTags);
    })
    .slice(0, limit);
}

export const CATEGORY_LABELS: Record<ArticleFrontmatter["category"], string> = {
  guides: "Guides",
  "market-insights": "Market Insights",
  "cost-breakdown": "Cost Breakdown",
  "product-news": "Product News",
};
